import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Pill, Phone, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ParacetamolScreeningResult {
  fullName: string;
  dateOfBirth: string;
  contactNumber: string;
  takenLast4Hours: boolean;
  under16: boolean;
  allergic: boolean;
  gpRestricted: boolean;
  blocked: boolean;
  hcpApproved: boolean;
  hcpName: string;
  hcpAuthorisationCode: string;
  clearedWithoutBlock: boolean;
}

interface MdtParacetamolModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (result: ParacetamolScreeningResult) => Promise<void>;
  submitting?: boolean;
}

const QUESTIONS = [
  { key: 'takenLast4Hours' as const, label: 'Have you taken Paracetamol in the last 4 hours?' },
  { key: 'under16' as const, label: 'Are you under 16 years of age?' },
  { key: 'allergic' as const, label: 'Are you aware of or know that you are allergic to Paracetamol?' },
  {
    key: 'gpRestricted' as const,
    label: 'Have you been told that you can not take paracetamol by your GP or any Health Care Professional?',
  },
];

function YesNoChoice({
  value,
  onChange,
  disabled,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="grid grid-cols-2 gap-2"
      role="group"
      aria-label="Yes or no"
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(false)}
        className={cn(
          'py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wide transition-colors',
          !value
            ? 'bg-white/15 text-white border-[1.5px] border-white/25'
            : 'bg-transparent text-white/35 border-[1.5px] border-white/[0.08]',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        No
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(true)}
        className={cn(
          'py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wide transition-colors',
          value
            ? 'bg-red-500 text-white border-[1.5px] border-red-400'
            : 'bg-transparent text-white/35 border-[1.5px] border-white/[0.08]',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        Yes
      </button>
    </div>
  );
}

const defaultAnswers = {
  takenLast4Hours: false,
  under16: false,
  allergic: false,
  gpRestricted: false,
};

type ModalStep = 'details' | 'screening';

export default function MdtParacetamolModal({
  open,
  onClose,
  onSubmit,
  submitting = false,
}: MdtParacetamolModalProps) {
  const [step, setStep] = useState<ModalStep>('details');
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [answers, setAnswers] = useState(defaultAnswers);
  const [hcpApproved, setHcpApproved] = useState(false);
  const [hcpName, setHcpName] = useState('');
  const [hcpAuthorisationCode, setHcpAuthorisationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const blocked = useMemo(
    () => answers.takenLast4Hours || answers.under16 || answers.allergic || answers.gpRestricted,
    [answers],
  );

  useEffect(() => {
    if (!open) {
      setStep('details');
      setFullName('');
      setDateOfBirth('');
      setContactNumber('');
      setAnswers(defaultAnswers);
      setHcpApproved(false);
      setHcpName('');
      setHcpAuthorisationCode('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!blocked) {
      setHcpApproved(false);
      setHcpName('');
      setHcpAuthorisationCode('');
    }
  }, [blocked]);

  useEffect(() => {
    if (hcpApproved && scrollRef.current) {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [hcpApproved]);

  if (!open) return null;

  const setAnswer = (key: keyof typeof defaultAnswers, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const buildResult = (clearedWithoutBlock: boolean): ParacetamolScreeningResult => ({
    fullName: fullName.trim(),
    dateOfBirth: dateOfBirth.trim(),
    contactNumber: contactNumber.trim(),
    ...answers,
    blocked,
    hcpApproved,
    hcpName: hcpName.trim(),
    hcpAuthorisationCode: hcpAuthorisationCode.trim(),
    clearedWithoutBlock,
  });

  const handleClearSubmit = async () => {
    setError(null);
    try {
      await onSubmit(buildResult(true));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save screening');
    }
  };

  const handleBlockedSubmit = async () => {
    if (!hcpApproved) {
      setError('HCP approval is required before administration');
      return;
    }
    if (!hcpName.trim()) {
      setError('Enter the HCP full name');
      return;
    }
    if (!hcpAuthorisationCode.trim()) {
      setError('Enter the HCPC/GMC authorisation code');
      return;
    }
    setError(null);
    try {
      await onSubmit(buildResult(false));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save screening');
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleContinueToScreening = () => {
    if (!fullName.trim()) {
      setError('Enter the patient full name');
      return;
    }
    if (!dateOfBirth.trim()) {
      setError('Enter date of birth');
      return;
    }
    if (!contactNumber.trim()) {
      setError('Enter contact number');
      return;
    }
    setError(null);
    setStep('screening');
  };

  const inputClass =
    'w-full px-3 py-2.5 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none';
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' };

  return createPortal(
    <div
      className="fixed inset-x-0 z-[500] flex flex-col"
      style={{
        top: '2.75rem',
        bottom: 'calc(4.25rem + env(safe-area-inset-bottom, 0px))',
        background: 'rgba(0,0,0,0.82)',
      }}
    >
      <div
        className="mx-auto w-full max-w-md flex flex-col min-h-0 h-full rounded-xl overflow-hidden m-2"
        style={{
          background: '#1a1d23',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 min-w-0">
            <Pill className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-[15px] font-extrabold text-white truncate">Paracetamol Screening</p>
          </div>
          <button onClick={handleClose} disabled={submitting} className="flex-shrink-0 p-1">
            <X className="w-4 h-4 text-white/40" />
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-2.5"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {step === 'details' ? (
            <div className="space-y-3">
              <p className="text-[11px] text-white/50 px-1">Patient details before screening</p>
              <div>
                <label className="block text-[11px] font-semibold text-white/60 mb-1">Full name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); setError(null); }}
                  placeholder="Patient full name"
                  className={inputClass}
                  style={inputStyle}
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-white/60 mb-1">Date of birth</label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => { setDateOfBirth(e.target.value); setError(null); }}
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-white/60 mb-1">Contact number</label>
                <input
                  type="tel"
                  value={contactNumber}
                  onChange={(e) => { setContactNumber(e.target.value); setError(null); }}
                  placeholder="Mobile or phone number"
                  className={inputClass}
                  style={inputStyle}
                  autoComplete="tel"
                />
              </div>
            </div>
          ) : (
            <>
          {QUESTIONS.map((q) => (
            <div
              key={q.key}
              className="p-3 rounded-xl space-y-2.5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <p className="text-[11px] font-medium text-white/85 leading-snug">{q.label}</p>
              <YesNoChoice
                value={answers[q.key]}
                onChange={(value) => setAnswer(q.key, value)}
                disabled={submitting}
              />
            </div>
          ))}

          {blocked && (
            <div
              className="rounded-2xl p-3 space-y-3"
              style={{ background: '#2a1218', border: '2px solid rgba(248,113,113,0.45)' }}
            >
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-base font-extrabold text-red-300 tracking-wide">DO NOT YET ADMINISTER</p>
                  <p className="text-[10px] text-red-200/70 mt-1">Change any answer above to No if entered in error.</p>
                </div>
              </div>

              <div className="rounded-xl p-3 space-y-1.5" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2 text-white">
                  <Phone className="w-4 h-4 text-blue-300 flex-shrink-0" />
                  <span className="text-sm font-bold font-mono">0203 051 1556</span>
                </div>
                <p className="text-[11px] text-white/80">Press <span className="font-bold text-white">7</span> on keypad</p>
                <p className="text-[11px] text-white/80">Press <span className="font-bold text-white">5</span> on keypad</p>
              </div>

              <p className="text-[10px] text-white/55 leading-relaxed">
                As a HCP you can disregard the above information. You must write your HCPC/GMC pin as the authorisation code along with your full name.
              </p>

              <div className="p-3 rounded-xl space-y-2.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="text-[11px] font-semibold text-white/90">
                  Has the administration of Paracetamol been approved?
                </p>
                <YesNoChoice value={hcpApproved} onChange={setHcpApproved} disabled={submitting} />
              </div>

              {hcpApproved && (
                <div className="space-y-2.5">
                  <div>
                    <label className="block text-[11px] font-semibold text-white/60 mb-1">Name</label>
                    <input
                      type="text"
                      value={hcpName}
                      onChange={(e) => setHcpName(e.target.value)}
                      placeholder="Full name"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-white/60 mb-1">Authorisation Code</label>
                    <input
                      type="text"
                      value={hcpAuthorisationCode}
                      onChange={(e) => setHcpAuthorisationCode(e.target.value)}
                      placeholder="HCPC / GMC PIN"
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>

        <div
          className="flex-shrink-0 px-3 py-3 space-y-2 border-t border-white/[0.06]"
          style={{ background: '#151922' }}
        >
          {error && (
            <p className="text-[11px] text-red-400 text-center">{error}</p>
          )}
          {step === 'details' ? (
            <button
              onClick={handleContinueToScreening}
              disabled={submitting}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50"
              style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd' }}
            >
              Continue to screening
            </button>
          ) : !blocked ? (
            <button
              onClick={handleClearSubmit}
              disabled={submitting}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50"
              style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.35)', color: '#4ade80' }}
            >
              {submitting ? 'Saving...' : 'Screening clear — continue'}
            </button>
          ) : (
            <button
              onClick={handleBlockedSubmit}
              disabled={submitting}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50"
              style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd' }}
            >
              {submitting ? 'Saving...' : 'Save HCP authorisation'}
            </button>
          )}
          {step === 'screening' && (
            <button
              type="button"
              onClick={() => { setStep('details'); setError(null); }}
              disabled={submitting}
              className="w-full py-2 text-[11px] font-semibold text-white/40"
            >
              Back to patient details
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
